import React, { useState } from "react";
import { Controller } from "react-hook-form";
import PlaceholderModal, { PlaceholderData } from "./PlaceHolderModal";
import { Button } from "@shared/ui/button";

export interface TextWithPlaceholderProps {
  name: string;
  control: any;
  label?: string;
  placeholder?: string;
  placeholders?: PlaceholderData;
  required?: boolean;
  helperText?: string;
  className?: string;
  minLength?: number;
  maxLength?: number;
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  disabled?: boolean;
  readonly?: boolean;
  onFieldChange?: (fieldName: string, value: any) => void;
}

export const TextWithPlaceholder: React.FC<TextWithPlaceholderProps> = ({
  name,
  control,
  label,
  placeholder,
  placeholders = [],
  required,
  helperText,
  className = "",
  minLength,
  maxLength,
  prefix,
  suffix,
  disabled,
  readonly,
  onFieldChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [hasPlaceholders, setHasPlaceholders] = useState(false);

  /**
   * Insert placeholder at cursor position and update form value
   */
  const handleInsertPlaceholder = (
    placeholderText: string,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;

    // Build new text
    const newText =
      currentValue.substring(0, start) +
      placeholderText +
      currentValue.substring(end);

    // Update form value using onChange from Controller
    onChange(newText);

    // Mark that field has placeholders (for visual feedback)
    setHasPlaceholders(newText.includes("{{") && newText.includes("}}"));

    // Call onFieldChange if provided
    if (onFieldChange) {
      onFieldChange(name, newText);
    }

    // Set cursor position after placeholder
    setTimeout(() => {
      const newPosition = start + placeholderText.length;
      input.setSelectionRange(newPosition, newPosition);
      input.focus();
    }, 0);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        // Check if current value has placeholders
        const currentValue = field.value || "";
        const valueHasPlaceholders =
          typeof currentValue === "string" &&
          currentValue.includes("{{") &&
          currentValue.includes("}}");

        return (
          <>
            {/* Button Row - Only if placeholders exist */}
            {placeholders && placeholders.length > 0 && (
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <Button
                type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(true)}
                >
                  <span className="text-base leading-none">+</span>
                  Insert Placeholder
                </Button>
              </div>
            )}

            {/* Input Field Container */}
            <div className="relative flex items-center">
              {prefix && (
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none text-sm">
                  {prefix}
                </span>
              )}

              <input
                {...field}
                ref={inputRef}
                id={name}
                type="text"
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readonly}
                minLength={minLength}
                onChange={(e) => {
                  field.onChange(e);
                  setHasPlaceholders(
                    e.target.value.includes("{{") && e.target.value.includes("}}")
                  );
                  if (onFieldChange) {
                    onFieldChange(name, e.target.value);
                  }
                }}
                aria-label={label}
                aria-required={required}
                className={`
                  w-full h-10 px-3 border rounded-lg
                  text-sm placeholder-gray-400
                  focus:outline-none transition-all duration-150
                  ${prefix ? "pl-8" : ""}
                  ${suffix ? "pr-8" : ""}
                  ${
                    disabled
                      ? "bg-gray-100 cursor-not-allowed text-gray-500 border-gray-300"
                      : "bg-white"
                  }
                  ${readonly ? "bg-gray-50 cursor-default border-gray-300" : ""}
                  ${
                    valueHasPlaceholders
                      ? "border-blue-400 bg-blue-50/30 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  }
                  ${className}
                `}
              />

              {suffix && (
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none text-sm">
                  {suffix}
                </span>
              )}
            </div>
            {/* Placeholder Modal */}
            <PlaceholderModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              placeholders={placeholders}
              onInsert={(placeholderText) => {
                handleInsertPlaceholder(
                  placeholderText,
                  field.value || "",
                  field.onChange
                );
                setIsModalOpen(false);
              }}
              fieldType="text-with-placeholder"
            />
          </>
        );
      }}
    />
  );
};

export default TextWithPlaceholder;
